// The Log hydration wheel and the chip row above it. client-app is the only
// consumer today, but the rules live here beside the glass arithmetic they
// extend, and they are pure so the spec can pin them.
//
// The wheel always runs 50 ml ticks from 50 ml to the daily cap. A drink's
// package sizes are inserted at their natural place as extra stops, and every
// stop carries a marker: glass ×N on whole glasses, the package kind on a
// size — the package wins where both apply, so Red Bull's 250 ml can shows the
// can, not glass ×1. The chip row offers the sizes (or three glasses when the
// drink has none) as shortcuts to those stops.

import { GLASS_ML, glassMultiple, MAX_DAILY_HYDRATION_ML } from './hydration-amount';

export const HYDRATION_WHEEL_STEP_ML = 50;

/** Shortcuts for a drink with no package sizes: one, two and three glasses. */
export const HYDRATION_GLASS_PRESETS_ML: readonly number[] = [GLASS_ML, GLASS_ML * 2, GLASS_ML * 3];

/**
 * Which kind marks a volume two sizes share (a 500 ml can beside a 500 ml
 * bottle): the first of these wins. Kinds this build does not know sort last.
 */
export const HYDRATION_PACKAGE_KIND_ORDER: readonly string[] = ['Can', 'Bottle', 'Pack', 'Cup'];

/** A package size as the API sends it — structurally HydrationPackageSizeModel. */
export interface HydrationPackageSize {
  kind?: string | null;
  milliliters?: number | null;
}

export type HydrationWheelMarker = { type: 'glass'; glasses: number } | { type: 'package'; kind: string };

export interface HydrationWheelStop {
  ml: number;
  /** Null on a plain 50 ml tick. */
  marker: HydrationWheelMarker | null;
}

export interface HydrationChipPreset {
  ml: number;
  marker: HydrationWheelMarker;
}

interface UsableSize {
  ml: number;
  kind: string;
}

/**
 * The sizes worth offering: a whole positive number of millilitres within the
 * daily cap, one per volume (the first by kind order), smallest first. Anything
 * else — a missing volume, a size no day could hold — is dropped rather than
 * drawn as a stop nobody can log.
 */
function usableSizes(sizes: readonly HydrationPackageSize[] | null | undefined): UsableSize[] {
  const kindRank = (kind: string) => {
    const index = HYDRATION_PACKAGE_KIND_ORDER.indexOf(kind);
    return index === -1 ? HYDRATION_PACKAGE_KIND_ORDER.length : index;
  };

  const candidates = (sizes ?? [])
    .map((size) => ({ ml: Math.round(size.milliliters ?? 0), kind: size.kind || 'Unknown' }))
    .filter((size) => Number.isFinite(size.ml) && size.ml > 0 && size.ml <= MAX_DAILY_HYDRATION_ML)
    .sort((a, b) => a.ml - b.ml || kindRank(a.kind) - kindRank(b.kind));

  const byMl = new Map<number, UsableSize>();
  for (const size of candidates) {
    if (!byMl.has(size.ml)) byMl.set(size.ml, size);
  }
  return [...byMl.values()];
}

/**
 * Every stop the wheel shows for a drink: the 50 ml ticks from 50 ml to the
 * daily cap with the drink's package sizes inserted where they fall, each stop
 * carrying its marker.
 */
export function hydrationWheelStops(sizes?: readonly HydrationPackageSize[] | null): HydrationWheelStop[] {
  const markers = new Map<number, HydrationWheelMarker | null>();

  for (let ml = HYDRATION_WHEEL_STEP_ML; ml <= MAX_DAILY_HYDRATION_ML; ml += HYDRATION_WHEEL_STEP_ML) {
    const glasses = glassMultiple(ml);
    markers.set(ml, glasses ? { type: 'glass', glasses } : null);
  }

  // Sizes go in last, so a package marker replaces the glass marker on a stop
  // both claim — the 250 ml can shows the can.
  for (const size of usableSizes(sizes)) {
    markers.set(size.ml, { type: 'package', kind: size.kind });
  }

  return [...markers.entries()].sort((a, b) => a[0] - b[0]).map(([ml, marker]) => ({ ml, marker }));
}

/**
 * The chips above the wheel: the drink's package sizes, or one, two and three
 * glasses when it has none. Only the sizes — never a glass chip beside them.
 */
export function hydrationChipPresets(sizes?: readonly HydrationPackageSize[] | null): HydrationChipPreset[] {
  const usable = usableSizes(sizes);

  if (usable.length === 0) {
    return HYDRATION_GLASS_PRESETS_ML.map((ml) => ({ ml, marker: { type: 'glass', glasses: glassMultiple(ml) ?? 1 } }));
  }

  return usable.map((size) => ({ ml: size.ml, marker: { type: 'package', kind: size.kind } }));
}

/**
 * The stop closest to an amount — for snapping an edited entry, or the current
 * amount when the drink (and so the stop list) changes. Ties go to the lower
 * stop; an empty list hands the amount back untouched.
 */
export function nearestHydrationStop(ml: number, stops: readonly HydrationWheelStop[]): number {
  if (stops.length === 0) return ml;
  return stops.reduce((best, stop) => (Math.abs(stop.ml - ml) < Math.abs(best - ml) ? stop.ml : best), stops[0].ml);
}
